import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft, Camera, Check } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import { useAuth } from '../../../context/AuthContext';
import ParsedQuestionsPreview from '../../../components/survey/ParsedQuestionsPreview';
import useQuestionParsingSocket from '../../../hooks/useQuestionParsingSocket';
import useRealtimeQuestionTags from '../../../hooks/useRealtimeQuestionTags';
import { createSimulation, handleApiError } from '../../../utils/simulationApi';
import { ParsedQuestion } from '../../../components/survey/types';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface InsightsFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void;
}

const SurveyAndFocusGroups: React.FC<InsightsFormProps> = ({ 
  onSubmit, 
  selectedSegmentIds, 
  personaFilters, 
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
}) => {
  const { audienceData } = useAudience();
  const { isAdmin } = useAuth();

  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  const [questionsText, setQuestionsText] = useState(initialFormData.questionsText || '');
  const [context, setContext] = useState(initialFormData.context || '');
  const [useInternalDebate, setUseInternalDebate] = useState(initialFormData.useInternalDebate || false);
  const [enableAdaptiveContext, setEnableAdaptiveContext] = useState(initialFormData.enableAdaptiveContext || false);
  
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>(
    initialFormData.parsedQuestions || []
  );
  const [previewDismissed, setPreviewDismissed] = useState(false);

  const [questionImages, setQuestionImages] = useState<Map<number, Array<{path: string, url: string, file: File}>>>(new Map());

  // --- Celery fallback for large / image-heavy surveys ---
  const handleParsingCompleted = React.useCallback((data: any) => {
    const qs: ParsedQuestion[] = data.parsed_questions || [];
    setParsedQuestions(qs);
    setPreviewDismissed(false);
  }, []);

  const handleParsingFailed = React.useCallback((data: any) => {
    console.error('Question parsing failed:', data);
    setError(data.error || data.detail || 'Failed to parse questions');
  }, []);

  const {
    parsingState,
    triggerParsing,
    clearParsingState,
    connect
  } = useQuestionParsingSocket({
    onParsingCompleted: handleParsingCompleted,
    onParsingFailed: handleParsingFailed,
    autoConnect: false
  });

  // --- Realtime parsing (always active unless Celery is running) ---
  const {
    parsedQuestions: realtimeParsed,
    isParsing,
    isUpdating,
    error: realtimeError,
    seedCache,
    reset: resetRealtimeHook,
  } = useRealtimeQuestionTags(
    questionsText,
    goal,
    context,
    !parsingState.isParsing,
  );

  // Sync realtime results into local state (no clearing on each keystroke)
  useEffect(() => {
    if (realtimeParsed.length > 0) {
      setParsedQuestions(realtimeParsed);
      setPreviewDismissed(false);
    } else if (!isParsing && !isUpdating && questionsText.trim() === '') {
      setParsedQuestions([]);
    }
  }, [realtimeParsed, isParsing, isUpdating, questionsText]);

  // Surface realtime parsing errors (e.g. raw_text length cap, rate limit)
  // to the form-level error banner so respondents/authors aren't stranded
  // on a silently-stuck textarea. We track the last realtime message we
  // wrote so we only clear the banner when the user fixes the issue, never
  // when another source (Celery / submit) populated it.
  const lastRealtimeErrorRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (realtimeError) {
      setError(realtimeError);
      lastRealtimeErrorRef.current = realtimeError;
    } else if (lastRealtimeErrorRef.current) {
      const lastWritten = lastRealtimeErrorRef.current;
      lastRealtimeErrorRef.current = null;
      setError((prev) => (prev === lastWritten ? null : prev));
    }
  }, [realtimeError]);

  // Keep the hook's cache in sync with local edits so re-parses preserve them
  useEffect(() => {
    if (parsedQuestions.length > 0) {
      seedCache(parsedQuestions);
    }
  }, [parsedQuestions, seedCache]);

  // Update parent with form data changes
  useEffect(() => {
    onFormDataChange({
      simName, goal, questionsText, context, useInternalDebate, enableAdaptiveContext
    });
  }, [simName, goal, questionsText, context, useInternalDebate, enableAdaptiveContext]);

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>, 
    value: string
  ) => {
    setter(value);
    if (onEditStep) onEditStep();
  };

  // Initialize images from initial data (edit & rerun)
  useEffect(() => {
    if (initialFormData.parsedQuestions && Array.isArray(initialFormData.parsedQuestions)) {
      const imageMap = new Map<number, Array<{path: string, url: string, file: File}>>();
      initialFormData.parsedQuestions.forEach((question: any, index: number) => {
        if (question.image_paths?.length > 0) {
          const images = question.image_paths.map((imagePath: string, imgIdx: number) => {
            let pathForComponent = imagePath;
            if (imagePath.startsWith('http')) {
              try { pathForComponent = new URL(imagePath).pathname; } catch { /* keep original */ }
            }
            return {
              path: imagePath,
              url: pathForComponent,
              file: new File([], `existing-image-${index}-${imgIdx}`, { type: 'image/jpeg' })
            };
          });
          imageMap.set(index, images);
        }
      });
      if (imageMap.size > 0) setQuestionImages(imageMap);
    }
  }, []);

  const saveMode = !!onSavePayload;
  const isFormValid = questionsText.trim() !== '' && (saveMode || selectedSegmentIds.length > 0);
  const totalImageCount = Array.from(questionImages.values()).reduce((sum, imgs) => sum + imgs.length, 0);
  const questionsWithImages = questionImages.size;
  const showPreview = parsedQuestions.length > 0 && !previewDismissed;

  // Celery fallback: trigger when images attached
  const triggerCeleryFallback = async () => {
    try {
      clearParsingState();
      setError(null);
      if (!parsingState.isConnected) await connect();

      const questions = parsedQuestions.length > 0
        ? parsedQuestions.map(q => q.text)
        : [questionsText.trim()].filter(Boolean);
      if (questions.length === 0) { setError('Please enter at least one question'); return; }

      const questionTypes: Record<number, string> = {};
      parsedQuestions.forEach((q, i) => { if (q.type) questionTypes[i] = q.type; });

      const imagePaths: Record<number, string[]> = {};
      questionImages.forEach((imgs, idx) => { imagePaths[idx] = imgs.map(img => img.path); });

      await triggerParsing(
        questions, goal, context,
        Object.keys(imagePaths).length > 0 ? imagePaths : undefined,
        false,
        Object.keys(questionTypes).length > 0 ? questionTypes : undefined,
      );
    } catch (err) {
      console.error('Error triggering Celery parsing:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse questions');
    }
  };

  const handleQuestionsUpdated = (questions: ParsedQuestion[]) => {
    setParsedQuestions(questions);
    seedCache(questions);
  };

  const handleUseParsedQuestions = (questions: ParsedQuestion[]) => {
    setParsedQuestions(questions);
    seedCache(questions);
  };

  const handleBackToQuestions = () => {
    setPreviewDismissed(true);
    resetRealtimeHook();
    clearParsingState();
  };

  const handleStartSimulation = async (questions: ParsedQuestion[]) => {
    const simulationData = {
      audience_id: audienceData.audienceId,
      segment_ids: selectedSegmentIds,
      name: simName,
      persona_filters: personaFilters,
      task: 'survey_and_focus_groups',
      questions: questions,
      context: context,
      goal: goal,
      use_parsed_questions: true,
      use_internal_debate: useInternalDebate,
      enable_adaptive_persona_context: enableAdaptiveContext
    };
    if (onSavePayload) { onSavePayload(simulationData); return; }
    const data = await createSimulation(simulationData);
    onSubmit(data.simulation_id);
  };

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;

    if (!showPreview) {
      if (totalImageCount > 0) {
        await triggerCeleryFallback();
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const questionsForSim = parsedQuestions.filter(q => q.type !== 'info_screen');
      const simulationData = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        name: simName,
        persona_filters: personaFilters,
        task: 'survey_and_focus_groups',
        questions: questionsForSim,
        context: context,
        goal: goal,
        use_parsed_questions: true,
        use_internal_debate: useInternalDebate,
        enable_adaptive_persona_context: enableAdaptiveContext
      };
      if (onSavePayload) { onSavePayload(simulationData); return; }
      const data = await createSimulation(simulationData);
      onSubmit(data.simulation_id);
    } catch (err) {
      console.error('Error starting simulation:', err);
      handleApiError(err, setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">{selectedSegmentIds.length} segments selected</span>
        </div>
        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
      </div>

      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => handleFieldChange(setSimName, e.target.value)}
          placeholder="Enter a name for your simulation"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
          Goal
        </label>
        <input
          id="goal"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={goal}
          onChange={(e) => handleFieldChange(setGoal, e.target.value)}
          placeholder="What do you want to achieve with this simulation?"
        />
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="questionsText" className="block text-sm font-medium text-gray-700">
            Survey or Focus Group Questions
          </label>
          <div className="flex items-center gap-3">
            {totalImageCount > 0 && (
              <span className="flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                <Camera className="w-3 h-3 mr-1" />
                {totalImageCount} {totalImageCount === 1 ? 'image' : 'images'} across {questionsWithImages} {questionsWithImages === 1 ? 'question' : 'questions'}
              </span>
            )}
            {showPreview && !isParsing && !isUpdating && (
              <div className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                <span className="text-xs text-green-600 font-medium">
                  {parsedQuestions.filter(q => q.type !== 'info_screen').length} question{parsedQuestions.filter(q => q.type !== 'info_screen').length !== 1 ? 's' : ''} parsed
                </span>
              </div>
            )}
            {isUpdating && !isParsing && (
              <span className="text-xs text-blue-600 font-medium">Re-parsing...</span>
            )}
          </div>
        </div>
        <textarea
          id="questionsText"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          rows={6}
          value={questionsText}
          onChange={(e) => handleFieldChange(setQuestionsText, e.target.value)}
          placeholder="Type your questions freely — AI will parse them into structured survey questions automatically"
        />

        {!showPreview && !isParsing && questionsText.trim() !== '' && (
          <p className="mt-2 text-xs text-gray-500">
            Type your questions — AI will automatically split, classify, and extract options, scales, and more.
          </p>
        )}

        {/* Single parsing indicator for both initial parse and re-parse */}
        {(isParsing || isUpdating) && (
          <div className="mt-4 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex gap-1">
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }} />
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms', animationDuration: '1s' }} />
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms', animationDuration: '1s' }} />
            </div>
            <span className="text-xs font-medium text-blue-700">
              {isUpdating ? 'AI is re-parsing questions...' : 'AI is parsing questions...'}
            </span>
          </div>
        )}

        {/* Celery fallback progress */}
        {parsingState.isParsing && (
          <div className="mt-3 p-3 bg-blue-50 rounded-md">
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-blue-700">Parsing with images...</span>
                  <span className="text-blue-600">{parsingState.progress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${parsingState.progress}%` }}
                  />
                </div>
                {parsingState.currentStep && (
                  <p className="text-xs text-blue-600 mt-1">{parsingState.currentStep}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Inline parsed questions preview -- stays visible during re-parse */}
        {showPreview && (
          <div className="mt-4">
            <ParsedQuestionsPreview
              parsedQuestions={parsedQuestions}
              onQuestionsUpdated={handleQuestionsUpdated}
              onUseQuestions={handleUseParsedQuestions}
              onStartSimulation={handleStartSimulation}
              onGoBack={handleBackToQuestions}
              isLoading={false}
              saveMode={saveMode}
            />
          </div>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-2">
          Context (Optional)
        </label>
        <textarea
          id="context"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          value={context}
          onChange={(e) => handleFieldChange(setContext, e.target.value)}
          placeholder="Any additional context about what you're trying to learn"
        />
      </div>

      {isAdmin && (
        <>
          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useInternalDebate}
                onChange={(e) => setUseInternalDebate(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Internal Debate</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              When enabled, each persona will go through an internal Proponent-Skeptic-Judge debate to generate more nuanced responses.
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enableAdaptiveContext}
                onChange={(e) => setEnableAdaptiveContext(e.target.checked)}
                className="w-4 h-4 text-[#07E5D1] bg-gray-100 border-gray-300 rounded focus:ring-[#07E5D1] focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Adaptive Persona Context</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              Analyzes each question to identify relevant persona fields, optimizes prompts, and validates if persona data is sufficient. Shows confidence scores in results.
            </p>
          </div>
        </>
      )}

      {/* Bottom CTA -- always at the bottom of the form */}
      <div className="flex justify-between items-center">
        <div>{error && <p className="text-sm text-red-600">{error}</p>}</div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting || parsingState.isParsing || isParsing}
          className="whitespace-nowrap"
        >
          {parsingState.isParsing ? 'Parsing...' : isParsing ? 'Detecting...' : isSubmitting ? (saveMode ? 'Saving...' : 'Starting...') : saveMode ? 'Continue' : 'Start Simulation'}
        </Button>
      </div>
    </div>
  );
};

export default SurveyAndFocusGroups;
